/** Orchestration evidence — thought, optional client message, then tool action. */

import { AgentToolbox } from "./AgentToolbox";
import { AgentMessageBubble } from "./AgentMessageBubble";
import { ChatThreadEvidence } from "./ChatThreadEvidence";
import type { AgentRun, FinishStep, ToolCallStep } from "./types";
import { ThoughtBubble } from "./ThoughtBubble";
import { ToolUsedLine } from "./ToolUsedLine";
import { toolOutcomesByName, toolOutcomeFromOutput } from "./toolStatus";

interface OrchestrationPhaseProps {
  run: AgentRun;
  steps: ToolCallStep[];
  /** Finish step — its thinking is the agent's reaction after the last action. */
  outcome: FinishStep | null;
}

function StepLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">{children}</p>
  );
}

export function OrchestrationPhase({ run, steps, outcome }: OrchestrationPhaseProps) {
  const agentName = run.agent_name?.trim() || "Agent";
  const usage = toolOutcomesByName(steps);
  const lastFailed = steps.length > 0 && toolOutcomeFromOutput(steps[steps.length - 1].output) === "error";
  const isChat = run.run_kind === "chat";
  const chatMessages = run.chat_messages ?? [];

  if (steps.length === 0) {
    return (
      <div className="mx-auto max-w-3xl space-y-6">
        {isChat && chatMessages.length > 0 ? (
          <div className="rounded-xl bg-white/[0.02] px-4 py-4 ring-1 ring-white/5">
            <p className="text-xs font-medium text-slate-300 mb-3">Conversation</p>
            <ChatThreadEvidence messages={chatMessages} agentName={agentName} />
          </div>
        ) : null}
        <p className="text-sm text-slate-400 py-2">
          {isChat
            ? "The worker didn’t call any tools — grade how they handled the conversation itself."
            : "The agent didn’t call any tools — it went straight to an outcome."}
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      {isChat && chatMessages.length > 0 ? (
        <div className="rounded-xl bg-white/[0.02] px-4 py-4 ring-1 ring-white/5">
          <p className="text-xs font-medium text-slate-300 mb-3">Conversation</p>
          <ChatThreadEvidence messages={chatMessages} agentName={agentName} />
        </div>
      ) : null}

      <div className="rounded-xl bg-white/[0.02] px-4 py-4 ring-1 ring-white/5">
        <p className="text-xs font-medium text-slate-300 mb-3">
          {isChat ? "Toolbox for this chat" : "Toolbox for this run"}
        </p>
        <AgentToolbox tools={run.available_tools} usage={usage} showLegend />
      </div>

      <div className="space-y-8">
        {steps.map((step, i) => (
          <div key={i} className="space-y-3 border-l border-white/10 pl-4">
            {step.thinking?.trim() ? (
              <div className="space-y-2">
                <StepLabel>1. The agent thought to itself</StepLabel>
                <ThoughtBubble agentName={agentName}>{step.thinking}</ThoughtBubble>
              </div>
            ) : null}

            {step.message?.trim() ? (
              <div className="space-y-2">
                <StepLabel>2. The agent messaged the client</StepLabel>
                <AgentMessageBubble html={step.message} agentName={agentName} />
              </div>
            ) : null}

            <div className="space-y-2">
              <StepLabel>
                {step.message?.trim() ? "3. The agent took an action" : "2. The agent took an action"}
              </StepLabel>
              <ToolUsedLine run={run} toolName={step.tool} output={step.output} />
            </div>
          </div>
        ))}
      </div>

      {outcome?.thinking?.trim() ? (
        <div className="space-y-2 border-l-2 border-amber-500/40 pl-4">
          <StepLabel>
            {lastFailed ? "What the agent thought right after the failure" : "What the agent thought next"}
          </StepLabel>
          {lastFailed ? (
            <p className="text-xs text-amber-300/80">
              The last action failed. Use this to judge whether the agent reacted or pressed on regardless.
            </p>
          ) : null}
          <ThoughtBubble agentName={agentName}>{outcome.thinking}</ThoughtBubble>
        </div>
      ) : null}
    </div>
  );
}
